/**
 * Fee Structure Management Page
 * Comprehensive fee management for schools:
 * - Define fee heads (Official/Unofficial)
 * - Set class-wise fees
 * - Create payment schemes (Quarterly, Half-yearly, One-time)
 * - Calculate NBFC eligible amount
 */
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fee Head Types with colors
const HEAD_TYPE_COLORS = {
  official: { bg: 'bg-green-100', text: 'text-green-700', badge: 'Official' },
  unofficial: { bg: 'bg-yellow-100', text: 'text-yellow-700', badge: 'Unofficial' }
};

// Default Fee Head Component
const FeeHeadCard = ({ head, amount, onChange, onToggle, isEnabled }) => (
  <div className={`border rounded-xl p-4 transition-all ${isEnabled ? 'border-orange-300 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-center gap-2">
        <input 
          type="checkbox" 
          checked={isEnabled}
          onChange={() => onToggle(head.head_id)}
          className="w-4 h-4 accent-orange-500"
        />
        <div>
          <h4 className="font-medium">{head.head_name}</h4>
          <p className="text-sm text-gray-500">{head.head_name_hi}</p>
        </div>
      </div>
      <div className="flex gap-1">
        <span className={`text-xs px-2 py-1 rounded-full ${HEAD_TYPE_COLORS[head.head_type]?.bg} ${HEAD_TYPE_COLORS[head.head_type]?.text}`}>
          {head.head_type === 'official' ? '✅ Official' : '⚠️ Unofficial'}
        </span>
        {head.nbfc_approved && (
          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
            🏦 NBFC
          </span>
        )}
        {head.is_refundable && (
          <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
            ↩️ Refundable
          </span>
        )}
      </div>
    </div>
    
    {isEnabled && (
      <div className="mt-3">
        <div className="flex items-center gap-2">
          <span className="text-gray-500">₹</span>
          <input
            type="number"
            value={amount || ''}
            onChange={(e) => onChange(head.head_id, Number(e.target.value))}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            placeholder="Amount"
          />
          <span className="text-xs text-gray-400 whitespace-nowrap">
            {head.frequency === 'one_time' ? '(One-time)' : 
             head.frequency === 'monthly' ? '(/month)' : '(/year)'}
          </span>
        </div>
      </div>
    )}
  </div>
);

// Payment Scheme Card
const SchemeCard = ({ scheme, onSelect, isSelected }) => (
  <div 
    onClick={() => onSelect(scheme)}
    className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
      isSelected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'
    }`}
  >
    <div className="flex justify-between items-start mb-2">
      <h4 className="font-bold">{scheme.scheme_name}</h4>
      {scheme.discount_percent > 0 && (
        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
          {scheme.discount_percent}% OFF
        </span>
      )}
    </div>
    <p className="text-sm text-gray-500 mb-3">{scheme.scheme_name_hi || scheme.description}</p>
    
    <div className="flex items-center gap-2">
      <span className={`px-2 py-1 rounded text-xs ${
        scheme.scheme_type === 'one_time' ? 'bg-blue-100 text-blue-700' :
        scheme.scheme_type === 'half_yearly' ? 'bg-purple-100 text-purple-700' :
        'bg-orange-100 text-orange-700'
      }`}>
        {scheme.scheme_type === 'one_time' ? 'एकमुश्त' :
         scheme.scheme_type === 'half_yearly' ? '2 किस्त' :
         scheme.scheme_type === 'quarterly' ? '4 किस्त' : '12 किस्त'}
      </span>
      <span className={`px-2 py-1 rounded text-xs ${
        scheme.approach === 'all_heads' ? 'bg-gray-100 text-gray-700' : 'bg-yellow-100 text-yellow-700'
      }`}>
        {scheme.approach === 'all_heads' ? 'All Fees' : 'Selected Fees'}
      </span>
    </div>
  </div>
);

// Main Component
export default function FeeStructurePage({ token, schoolId }) {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('structure');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Fee Structure State
  const [defaultHeads, setDefaultHeads] = useState([]);
  const [enabledHeads, setEnabledHeads] = useState({});
  const [feeAmounts, setFeeAmounts] = useState({});
  const [selectedClass, setSelectedClass] = useState('Class 1');
  const [classFees, setClassFees] = useState({});
  
  // Payment Schemes State
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [newScheme, setNewScheme] = useState({
    scheme_name: '',
    scheme_type: 'quarterly',
    approach: 'all_heads',
    discount_percent: 0
  });
  
  // NBFC Calculation State
  const [nbfcCalc, setNbfcCalc] = useState(null);
  
  const classes = ['Nursery', 'LKG', 'UKG', ...Array.from({length: 12}, (_, i) => `Class ${i + 1}`)];

  useEffect(() => {
    loadData();
  }, [schoolId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load default fee heads
      const headsRes = await fetch(`${API_URL}/api/fee-structure/heads/default`);
      const headsData = await headsRes.json();
      if (headsData.success) {
        setDefaultHeads(headsData.fee_heads);
        // Enable all official heads by default
        const enabled = {};
        headsData.fee_heads.forEach(h => {
          enabled[h.head_id] = h.head_type === 'official';
        });
        setEnabledHeads(enabled);
      }

      // Load existing structure if any
      if (schoolId) {
        const structRes = await fetch(`${API_URL}/api/fee-structure/${schoolId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const structData = await structRes.json();
        if (structData.has_structure) {
          // Populate existing data
          const existingEnabled = {};
          const existingAmounts = {};
          
          structData.structure.fee_heads?.forEach(h => {
            existingEnabled[h.head_id] = true;
          });
          
          structData.structure.class_fees?.forEach(cf => {
            existingAmounts[cf.class_name] = cf.fees || {};
          });
          
          setEnabledHeads(existingEnabled);
          setClassFees(existingAmounts);
          if (existingAmounts[selectedClass]) {
            setFeeAmounts(existingAmounts[selectedClass]);
          }
        }

        // Load schemes
        const schemesRes = await fetch(`${API_URL}/api/fee-structure/schemes/${schoolId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const schemesData = await schemesRes.json();
        if (schemesData.schemes?.length > 0) {
          setSchemes(schemesData.schemes);
        } else if (schemesData.default_templates) {
          setSchemes(schemesData.default_templates);
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
    setLoading(false);
  };

  const handleToggleHead = (headId) => {
    setEnabledHeads(prev => ({ ...prev, [headId]: !prev[headId] }));
  };

  const handleAmountChange = (headId, amount) => {
    setFeeAmounts(prev => ({ ...prev, [headId]: amount }));
  };

  const handleClassChange = (className) => {
    // Save current class fees
    setClassFees(prev => ({ ...prev, [selectedClass]: feeAmounts }));
    // Load new class fees
    setSelectedClass(className);
    setFeeAmounts(classFees[className] || {});
  };

  const handleSaveStructure = async () => {
    setSaving(true);
    try {
      // Save current class first
      const updatedClassFees = { ...classFees, [selectedClass]: feeAmounts };
      
      // Prepare fee heads
      const enabledFeeHeads = defaultHeads.filter(h => enabledHeads[h.head_id]);
      
      // Prepare class fees
      const classFeesArray = Object.entries(updatedClassFees).map(([className, fees]) => ({
        class_name: className,
        fees: fees
      }));

      const res = await fetch(`${API_URL}/api/fee-structure/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          school_id: schoolId || 'default',
          academic_year: '2025-26',
          fee_heads: enabledFeeHeads,
          class_fees: classFeesArray
        })
      });

      const data = await res.json();
      if (data.success) {
        alert('Fee structure saved successfully!');
        setClassFees(updatedClassFees);
      }
    } catch (err) {
      console.error('Error saving:', err);
      alert('Error saving fee structure');
    }
    setSaving(false);
  };

  const calculateNBFCEligible = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/fee-structure/${schoolId || 'default'}/nbfc-eligible/${selectedClass}`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      const data = await res.json();
      if (data.success) {
        setNbfcCalc(data);
      }
    } catch (err) {
      console.error('Error calculating NBFC:', err);
    }
  };

  const createScheme = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fee-structure/scheme/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          school_id: schoolId || 'default',
          ...newScheme
        })
      });
      const data = await res.json();
      if (data.success) {
        alert('Payment scheme created!');
        loadData();
        setNewScheme({ scheme_name: '', scheme_type: 'quarterly', approach: 'all_heads', discount_percent: 0 });
      }
    } catch (err) {
      console.error('Error creating scheme:', err);
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    let official = 0, unofficial = 0, nbfcApproved = 0;
    
    defaultHeads.forEach(head => {
      if (enabledHeads[head.head_id]) {
        const amount = feeAmounts[head.head_id] || 0;
        if (head.head_type === 'official') official += amount;
        else unofficial += amount;
        if (head.nbfc_approved && !head.is_refundable) nbfcApproved += amount;
      }
    });
    
    return { official, unofficial, nbfcApproved, total: official + unofficial };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="fee-structure-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            💰 {language === 'hi' ? 'शुल्क संरचना' : 'Fee Structure'}
          </h1>
          <p className="text-gray-500">
            {language === 'hi' ? 'Fee heads और Payment schemes manage करें' : 'Manage fee heads and payment schemes'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {[
          { id: 'structure', label: 'Fee Structure', labelHi: 'शुल्क संरचना', icon: '📋' },
          { id: 'schemes', label: 'Payment Schemes', labelHi: 'भुगतान योजना', icon: '📅' },
          { id: 'nbfc', label: 'NBFC Calculator', labelHi: 'NBFC कैलकुलेटर', icon: '🏦' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 font-medium transition-colors ${
              activeTab === tab.id 
                ? 'text-orange-500 border-b-2 border-orange-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.icon} {language === 'hi' ? tab.labelHi : tab.label}
          </button>
        ))}
      </div>

      {/* Fee Structure Tab */}
      {activeTab === 'structure' && (
        <div className="space-y-6">
          {/* Class Selector */}
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <label className="block text-sm font-medium mb-2">Select Class</label>
            <div className="flex gap-2 flex-wrap">
              {classes.map(cls => (
                <button
                  key={cls}
                  onClick={() => handleClassChange(cls)}
                  className={`px-3 py-2 rounded-lg text-sm transition ${
                    selectedClass === cls 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {cls}
                </button>
              ))}
            </div>
          </div>

          {/* Totals Summary */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <p className="text-sm text-green-600">Official Fees</p>
              <p className="text-2xl font-bold text-green-700">₹{totals.official.toLocaleString()}</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
              <p className="text-sm text-yellow-600">Unofficial Fees</p>
              <p className="text-2xl font-bold text-yellow-700">₹{totals.unofficial.toLocaleString()}</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <p className="text-sm text-blue-600">NBFC Eligible</p>
              <p className="text-2xl font-bold text-blue-700">₹{totals.nbfcApproved.toLocaleString()}</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
              <p className="text-sm text-orange-600">Total Fee</p>
              <p className="text-2xl font-bold text-orange-700">₹{totals.total.toLocaleString()}</p>
            </div>
          </div>

          {/* Fee Heads Grid */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Fee Heads for {selectedClass}</h3>
              <div className="text-sm text-gray-500">
                {Object.values(enabledHeads).filter(Boolean).length} of {defaultHeads.length} enabled
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {defaultHeads.map(head => (
                <FeeHeadCard
                  key={head.head_id}
                  head={head}
                  amount={feeAmounts[head.head_id]}
                  onChange={handleAmountChange}
                  onToggle={handleToggleHead}
                  isEnabled={enabledHeads[head.head_id]}
                />
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                // Copy to all classes
                const confirmed = window.confirm('Copy this fee structure to all classes?');
                if (confirmed) {
                  const newClassFees = {};
                  classes.forEach(cls => {
                    newClassFees[cls] = { ...feeAmounts };
                  });
                  setClassFees(newClassFees);
                  alert('Copied to all classes!');
                }
              }}
              className="px-6 py-3 border border-orange-500 text-orange-500 rounded-xl hover:bg-orange-50"
            >
              📋 Copy to All Classes
            </button>
            <button
              onClick={handleSaveStructure}
              disabled={saving}
              className="px-6 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:opacity-50"
            >
              {saving ? '⏳ Saving...' : '💾 Save Fee Structure'}
            </button>
          </div>
        </div>
      )}

      {/* Payment Schemes Tab */}
      {activeTab === 'schemes' && (
        <div className="space-y-6">
          {/* Existing Schemes */}
          <div>
            <h3 className="font-bold text-lg mb-4">Available Payment Schemes</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {schemes.map((scheme, idx) => (
                <SchemeCard
                  key={idx}
                  scheme={scheme}
                  isSelected={selectedScheme?.scheme_name === scheme.scheme_name}
                  onSelect={setSelectedScheme}
                />
              ))}
            </div>
          </div>

          {/* Create New Scheme */}
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <h3 className="font-bold text-lg mb-4">➕ Create New Scheme</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Scheme Name</label>
                <input
                  type="text"
                  value={newScheme.scheme_name}
                  onChange={(e) => setNewScheme({...newScheme, scheme_name: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Early Bird Quarterly"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Scheme Type</label>
                <select
                  value={newScheme.scheme_type}
                  onChange={(e) => setNewScheme({...newScheme, scheme_type: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="one_time">One-Time (एकमुश्त)</option>
                  <option value="half_yearly">Half-Yearly (छमाही)</option>
                  <option value="quarterly">Quarterly (त्रैमासिक)</option>
                  <option value="monthly">Monthly (मासिक)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Approach</label>
                <select
                  value={newScheme.approach}
                  onChange={(e) => setNewScheme({...newScheme, approach: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="all_heads">All Fee Heads (Total)</option>
                  <option value="fixed_heads">Fixed Heads Only</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Discount %</label>
                <input
                  type="number"
                  value={newScheme.discount_percent}
                  onChange={(e) => setNewScheme({...newScheme, discount_percent: Number(e.target.value)})}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="5"
                  min="0"
                  max="20"
                />
              </div>
            </div>
            
            <button
              onClick={createScheme}
              disabled={!newScheme.scheme_name}
              className="mt-4 px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 disabled:opacity-50"
            >
              ➕ Create Scheme
            </button>
          </div>

          {/* Scheme Types Explanation */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <h4 className="font-bold text-blue-800 mb-2">📋 Payment Scheme Types</h4>
            <div className="grid grid-cols-2 gap-4 text-sm text-blue-700">
              <div>
                <p><strong>All Heads:</strong> Total fees को scheme में divide करो</p>
                <p className="text-xs text-blue-500">Example: ₹60,000 → 4 quarterly = ₹15,000 each</p>
              </div>
              <div>
                <p><strong>Fixed Heads:</strong> सिर्फ selected heads को scheme में</p>
                <p className="text-xs text-blue-500">Example: Tuition + Exam only, Transport separate</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NBFC Calculator Tab */}
      {activeTab === 'nbfc' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <h3 className="text-xl font-bold mb-2">🏦 NBFC Loan Calculator</h3>
            <p className="opacity-80">Calculate how much loan amount will be approved by NBFC</p>
          </div>

          {/* Class Selection */}
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <label className="block text-sm font-medium mb-2">Select Class for Calculation</label>
            <div className="flex gap-2 flex-wrap">
              {classes.slice(0, 10).map(cls => (
                <button
                  key={cls}
                  onClick={() => setSelectedClass(cls)}
                  className={`px-3 py-2 rounded-lg text-sm transition ${
                    selectedClass === cls 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {cls}
                </button>
              ))}
            </div>
            
            <button
              onClick={calculateNBFCEligible}
              className="mt-4 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600"
            >
              🧮 Calculate NBFC Eligible Amount
            </button>
          </div>

          {/* NBFC Calculation Result */}
          {nbfcCalc && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 rounded-xl p-4 border border-green-200 text-center">
                  <p className="text-sm text-green-600">NBFC Eligible</p>
                  <p className="text-3xl font-bold text-green-700">
                    ₹{nbfcCalc.nbfc_calculation?.eligible_amount?.toLocaleString()}
                  </p>
                  <p className="text-xs text-green-500">{nbfcCalc.nbfc_calculation?.coverage_percent}% of total</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 border border-red-200 text-center">
                  <p className="text-sm text-red-600">Not Covered</p>
                  <p className="text-3xl font-bold text-red-700">
                    ₹{nbfcCalc.nbfc_calculation?.excluded_amount?.toLocaleString()}
                  </p>
                  <p className="text-xs text-red-500">Parents pay directly</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-center">
                  <p className="text-sm text-gray-600">Total Fee</p>
                  <p className="text-3xl font-bold text-gray-700">
                    ₹{nbfcCalc.nbfc_calculation?.total_fee?.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Eligible Heads */}
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <h4 className="font-bold text-green-700 mb-3">✅ NBFC Approved Heads</h4>
                <div className="space-y-2">
                  {nbfcCalc.eligible_heads?.map((head, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b">
                      <div>
                        <span className="font-medium">{head.head_name}</span>
                        <span className="text-gray-500 text-sm ml-2">({head.head_name_hi})</span>
                      </div>
                      <span className="font-bold text-green-600">₹{head.amount?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Excluded Heads */}
              {nbfcCalc.excluded_heads?.length > 0 && (
                <div className="bg-white rounded-xl p-4 shadow-sm border">
                  <h4 className="font-bold text-red-700 mb-3">❌ Not Covered by NBFC</h4>
                  <div className="space-y-2">
                    {nbfcCalc.excluded_heads?.map((head, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b">
                        <div>
                          <span className="font-medium">{head.head_name}</span>
                          <span className="text-xs text-red-500 ml-2">({head.exclusion_reason})</span>
                        </div>
                        <span className="font-bold text-red-600">₹{head.amount?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Note */}
              <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                <p className="text-yellow-800 text-sm">
                  <strong>⚠️ Note:</strong> {nbfcCalc.note}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
